/**
 * Boot Safety
 *
 * Wraps each Quasar boot file's default export so that a thrown error inside
 * a boot function does NOT propagate to Quasar's `client-entry.js` try/catch —
 * which would early-return without calling `app.mount('#q-app')` and leave
 * the `#app-loading` spinner on screen forever.
 *
 * Instead, errors are:
 *   - logged to console
 *   - persisted to localStorage (last N errors, for diagnostics)
 *   - exposed via `window.__bootHadErrors` so the mounted App can surface a banner
 *   - swallowed, so the boot chain continues and the app still mounts
 *
 * Also forwards preboot errors (captured before Faro/Sentry init by the
 * inline script in index.html) to the persisted error log on first use.
 *
 * See:
 *   - `.quasar/dev-spa/client-entry.js` (the boot loop we're protecting against)
 *   - `src/components/BootErrorBanner.vue` (UI surface)
 *   - `index.html` (preboot capture inline script)
 */

const MAX_PERSISTED_ERRORS = 5;
const STORAGE_KEY = 'docbits_boot_errors';
const PREBOOT_STORAGE_KEY = 'docbits_preboot_errors';
const BANNER_DISMISS_KEY = 'docbits_boot_banner_dismissed_at';

export interface PersistedBootError {
  bootName: string;
  timestamp: string;
  message: string;
  stack?: string;
  userAgent: string;
  appVersion: string;
  url: string;
}

export interface PrebootError {
  type: 'error' | 'rejection';
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  timestamp: number;
}

declare global {
  interface Window {
    __bootHadErrors?: boolean;
    __bootErrorNames?: string[];
    __prebootErrors?: PrebootError[];
    __appInitialized?: boolean;
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readJSON<T>(key: string, fallback: T): T {
  const ls = safeLocalStorage();
  if (!ls) return fallback;
  try {
    const raw = ls.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(key, JSON.stringify(value));
  } catch {
    // quota exceeded or disabled — non-fatal
  }
}

function appVersion(): string {
  return (
    process.env.APP_VUE_CURRENT_VERSION ||
    process.env.APP_VERSION ||
    'unknown'
  ).toString();
}

export function persistBootError(bootName: string, err: unknown): void {
  const entry: PersistedBootError = {
    bootName,
    timestamp: new Date().toISOString(),
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    appVersion: appVersion(),
    url: typeof window !== 'undefined' ? window.location.href : '',
  };
  const existing = readJSON<PersistedBootError[]>(STORAGE_KEY, []);
  existing.unshift(entry);
  writeJSON(STORAGE_KEY, existing.slice(0, MAX_PERSISTED_ERRORS));

  if (typeof window !== 'undefined') {
    window.__bootHadErrors = true;
    if (!window.__bootErrorNames) window.__bootErrorNames = [];
    window.__bootErrorNames.push(bootName);
  }
}

export function getBootErrors(): PersistedBootError[] {
  return readJSON<PersistedBootError[]>(STORAGE_KEY, []);
}

export function clearBootErrors(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function takePrebootErrors(): PrebootError[] {
  const fromLs = readJSON<PrebootError[]>(PREBOOT_STORAGE_KEY, []);
  const fromWindow =
    typeof window !== 'undefined' && Array.isArray(window.__prebootErrors)
      ? window.__prebootErrors
      : [];
  const combined = [...fromLs, ...fromWindow];

  const ls = safeLocalStorage();
  if (ls) {
    try {
      ls.removeItem(PREBOOT_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  if (typeof window !== 'undefined') {
    window.__prebootErrors = [];
  }
  return combined;
}

export function isBannerDismissed(): boolean {
  const ls = safeLocalStorage();
  if (!ls) return false;
  try {
    const raw = ls.getItem(BANNER_DISMISS_KEY);
    if (!raw) return false;
    // Dismissals only last 30 minutes, so a persistent hang still re-surfaces
    // the banner on the next session without nagging within a session.
    return Date.now() - Number(raw) < 30 * 60 * 1000;
  } catch {
    return false;
  }
}

export function dismissBanner(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(BANNER_DISMISS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

/**
 * Unregister service workers + clear workbox caches, then reload.
 * Preserves localStorage/sessionStorage/cookies so the user stays logged in.
 * If the root cause is stale stored state (not SW cache), a second click or
 * manual "Clear site data" is still needed — but the common case (bad SW
 * cached chunk) resolves with this alone.
 */
export async function hardReloadClearingCaches(): Promise<void> {
  const timeout = <T>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
    Promise.race([
      p,
      new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
    ]);

  try {
    if ('serviceWorker' in navigator) {
      const regs = await timeout(
        navigator.serviceWorker.getRegistrations(),
        3000,
        [] as readonly ServiceWorkerRegistration[],
      );
      await timeout(
        Promise.all(regs.map((r) => r.unregister())),
        3000,
        [] as boolean[],
      );
    }
  } catch (err) {
    console.warn('[BootSafety] Service worker unregister failed:', err);
  }

  try {
    if (typeof caches !== 'undefined') {
      const names = await timeout(caches.keys(), 3000, [] as string[]);
      await timeout(
        Promise.all(names.map((n) => caches.delete(n))),
        3000,
        [] as boolean[],
      );
    }
  } catch (err) {
    console.warn('[BootSafety] Cache clear failed:', err);
  }

  clearBootErrors();

  const url = new URL(window.location.href);
  url.searchParams.set('_boot_recover', String(Date.now()));
  window.location.href = url.toString();
}

type BootArgs = Parameters<(args: unknown) => unknown>[0];
type BootFn<T extends unknown = unknown> = (args: BootArgs) => T | Promise<T>;

/**
 * Wrap a boot file's default-export function so it never throws upward.
 *
 * Usage:
 *   export default safeBoot('websocket', async ({ app, router }) => {
 *     // existing body
 *   });
 *
 * Or, with Quasar's type helper:
 *   export default boot(safeBoot('faro', async ({ app, router }) => { ... }));
 */
export function safeBoot<T>(name: string, fn: BootFn<T>): BootFn<T | undefined> {
  return async (args: BootArgs) => {
    try {
      return await fn(args);
    } catch (err) {
      // Log but do not rethrow — rethrowing would break app.mount().
      // eslint-disable-next-line no-console
      console.error(`[Boot:${name}] threw during init — app will continue in degraded mode:`, err);
      persistBootError(name, err);
      return undefined;
    }
  };
}
